import React, { useState, useEffect } from "react";
import { HeaderSection } from "@/components/shared/header-section";
import { PageContent } from "@/components/shared/page-content";
import { useLazyQuery } from "@apollo/client";
import { DataTableManual } from "@/components/shared/data-table-manual/data-table-manual";
import { columns } from "./components/columns";
import { DataTableToolbar } from "@/components/shared/data-table-manual/data-table-toolbar-manual";
import { Button } from "@/components/ui/button";
import {
  DELETE_MANY_FABRIC_PALETTES,
  GET_FABRIC_PALETTES,
} from "./fabricPaletteQueries";
import useOpenDrawer from "@/hooks/useOpenDrawer";
import { useDeleteConfirmation } from "@/hooks/useDeleteConfirmation";
import { getOrderSortParams } from "@/utils/orderUtils";
import FabricPaletteForm from "./components/FabricPaletteForm";

export default function FabricPalettes() {
  const [searchTerm, setSearchTerm] = useState("");
  const [tableData, setTableData] = useState([]);
  const [tableDataCount, setTableDataCount] = useState(0);
  const [order, setOrder] = useState([]);
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  );

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const [loadFabricPalettes, { data, error, loading }] = useLazyQuery(
    GET_FABRIC_PALETTES,
    {
      variables: {},
      fetchPolicy: "cache-and-network",
    }
  );

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_FABRIC_PALETTES,
    refetchQueries: ["GET_FABRIC_PALETTES"],
    messages: {
      success: "Fabric palettes deleted successfully",
      error: "Failed to delete fabric palettes",
    },
    itemName: "fabric palette",
  });

  useEffect(() => {
    loadFabricPalettes({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
      },
    });
  }, [searchTerm, pagination, order]);

  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 });
  }, [searchTerm, pagination.pageSize, order]);

  useEffect(() => {
    if (data) {
      setTableData(data.findManyFabricPalette);
      setTableDataCount(data.findManyFabricPaletteCount ?? 0);
    }
  }, [data]);

  const openFabricPaletteForm = useOpenDrawer(FabricPaletteForm);

  return (
    <PageContent>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <HeaderSection title="Fabric Palettes" />
        <Button onClick={openFabricPaletteForm}>Create New +</Button>
      </div>

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={openFabricPaletteForm}
        isLoading={loading}
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: "Filter palettes...",
                accessor: "name",
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [],
            }}
          />
        )}
      />
    </PageContent>
  );
}
